// UV-print font catalog: the decorative "design" fonts (Google equivalents of
// the print shop's School Days proof sheet) plus every font the engraved plate
// offers. UV-only — the engraved-plate editors keep using FONT_OPTIONS.
//
// Each design has a pre-rendered 75x75 swatch PNG (font-swatches/, generated
// headlessly so the picker never waits on webfont loads). UV_FONT_SWATCHES is
// keyed by font id; a design without a swatch falls back to a live-rendered
// square using SAMPLE_NAMES + the font family (see uv-print.tsx).
import {Font, FONT_FAMILY_MAP} from '../engraved-plates/ep-extended/engraved-plate-extended.store';
import swEBGaramond from './font-swatches/eb-garamond.png';
import swGreatVibes from './font-swatches/great-vibes.png';
import swCinzel from './font-swatches/cinzel.png';
import swPlayfairDisplay from './font-swatches/playfair-display.png';
import swPinyonScript from './font-swatches/pinyon-script.png';
import swGraduate from './font-swatches/graduate.png';
import swPacifico from './font-swatches/pacifico.png';
import swLobsterTwo from './font-swatches/lobster-two.png';
import swJost from './font-swatches/jost.png';
import swYellowtail from './font-swatches/yellowtail.png';
import swSacramento from './font-swatches/sacramento.png';
import swRoboto from './font-swatches/roboto.png';
import swLibreBaskerville from './font-swatches/libre-baskerville.png';
import swCarlito from './font-swatches/carlito.png';
import swTimesNewRoman from './font-swatches/times-new-roman.png';
import swOldStandardTT from './font-swatches/old-standard-tt.png';
import swArial from './font-swatches/arial.png';
import swNunitoSans from './font-swatches/nunito-sans.png';
import swCrimsonText from './font-swatches/crimson-text.png';
import swIBMPlexMono from './font-swatches/ibm-plex-mono.png';

export type UVFontOption = {
    value: Font;
    label: string;
};

// Decorative designs first (proof-sheet order), then the engraved-plate fonts.
export const UV_FONT_OPTIONS: UVFontOption[] = [
    {value: Font.EBGaramond, label: 'EB Garamond'}, // Garamond Premier Pro
    {value: Font.GreatVibes, label: 'Great Vibes'}, // Snell Roundhand
    {value: Font.Cinzel, label: 'Cinzel'}, // AdornS Serif (Bold Block)
    {value: Font.PlayfairDisplay, label: 'Playfair Display'}, // Didot
    {value: Font.PinyonScript, label: 'Pinyon Script'}, // Ballantines
    {value: Font.Graduate, label: 'Graduate'}, // Novecento slab condensed
    {value: Font.Pacifico, label: 'Pacifico'}, // Gelato Luxe (Monterey)
    {value: Font.LobsterTwo, label: 'Lobster Two'}, // Funkydori
    {value: Font.Jost, label: 'Jost'}, // Futura medium
    {value: Font.Yellowtail, label: 'Yellowtail'}, // CornerStoreJF
    {value: Font.Sacramento, label: 'Sacramento'}, // SantElia Script light
    {value: Font.Roboto, label: 'Roboto'},
    {value: Font.LibreBaskerville, label: 'Libre Baskerville'},
    {value: Font.Carlito, label: 'Carlito'},
    {value: Font.TimesNewRoman, label: 'Times New Roman'},
    {value: Font.OldStandardTT, label: 'Old Standard TT'},
    {value: Font.Arial, label: 'Arial'},
    {value: Font.NunitoSans, label: 'Nunito Sans'},
    {value: Font.CrimsonText, label: 'Crimson Text'},
    {value: Font.IBMPlexMono, label: 'IBM Plex Mono'},
];

// Pre-rendered 75x75 swatches. A font missing here (e.g. one added later)
// gets a live-rendered square instead.
export const UV_FONT_SWATCHES: Partial<Record<Font, string>> = {
    [Font.EBGaramond]: swEBGaramond,
    [Font.GreatVibes]: swGreatVibes,
    [Font.Cinzel]: swCinzel,
    [Font.PlayfairDisplay]: swPlayfairDisplay,
    [Font.PinyonScript]: swPinyonScript,
    [Font.Graduate]: swGraduate,
    [Font.Pacifico]: swPacifico,
    [Font.LobsterTwo]: swLobsterTwo,
    [Font.Jost]: swJost,
    [Font.Yellowtail]: swYellowtail,
    [Font.Sacramento]: swSacramento,
    [Font.Roboto]: swRoboto,
    [Font.LibreBaskerville]: swLibreBaskerville,
    [Font.Carlito]: swCarlito,
    [Font.TimesNewRoman]: swTimesNewRoman,
    [Font.OldStandardTT]: swOldStandardTT,
    [Font.Arial]: swArial,
    [Font.NunitoSans]: swNunitoSans,
    [Font.CrimsonText]: swCrimsonText,
    [Font.IBMPlexMono]: swIBMPlexMono,
};

// Sample first names shown in the swatch squares (matches the generated PNGs;
// also used for the live-render fallback when a swatch image is missing).
export const UV_FONT_SAMPLE_NAMES: Partial<Record<Font, string>> = {
    [Font.EBGaramond]: 'Ryan',
    [Font.GreatVibes]: 'Greg',
    [Font.Cinzel]: 'ANDREA',
    [Font.PlayfairDisplay]: 'Jess',
    [Font.PinyonScript]: 'Izzy',
    [Font.Graduate]: 'JANE',
    [Font.Pacifico]: 'Olivia',
    [Font.LobsterTwo]: 'Beth',
    [Font.Jost]: 'MAX',
    [Font.Yellowtail]: 'Anna',
    [Font.Sacramento]: 'Mary',
    [Font.Roboto]: 'Sam',
    [Font.LibreBaskerville]: 'Lily',
    [Font.Carlito]: 'Noah',
    [Font.TimesNewRoman]: 'Emma',
    [Font.OldStandardTT]: 'Owen',
    [Font.Arial]: 'Kate',
    [Font.NunitoSans]: 'Leo',
    [Font.CrimsonText]: 'Cora',
    [Font.IBMPlexMono]: 'Ada',
};

// Names used when a font has no entry in UV_FONT_SAMPLE_NAMES: pick a
// pseudo-random (but stable per font id, so it doesn't change every render)
// name for the live-rendered square.
const RANDOM_SAMPLE_NAMES = ['Ryan', 'Greg', 'Jess', 'Izzy', 'Jane', 'Olivia', 'Lauryl', 'Emma', 'Noah', 'Mary'];

export const uvFontSampleName = (fontId: Font): string => {
    return UV_FONT_SAMPLE_NAMES[fontId] || RANDOM_SAMPLE_NAMES[Math.abs(fontId) % RANDOM_SAMPLE_NAMES.length];
};

export const uvFontLabel = (fontId: Font): string => {
    return UV_FONT_OPTIONS.find(f => f.value === fontId)?.label || FONT_FAMILY_MAP[fontId]?.split(',')[0]?.replace(/"/g, '') || `Font ${fontId}`;
};
